import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { archiveClassPack, createClassPack, listClassPacks, searchClassLoops, updateClassPack } from '@/api/classPacks';
import { bpmsCompatible, isValidBpm } from '@/audio/clipTiming';
import { CLASS_GROUP_META, CLASS_GROUPS } from '@/state/classSession';

const LIMITS = { melody: 20, drums: 10, texture: 10, fx: 10 };
const FILL_PAGE_SIZE = 100;
const FILL_CATALOG_CAP = 1000;
const emptySlots = () => ({ melody: [], drums: [], texture: [], fx: [] });
const snapshotLoop = (loop) => ({
  id: String(loop.id), name: String(loop.name), bpm: Number(loop.bpm), bars: Number(loop.bars),
  key: loop.key || null, instrument_group: String(loop.instrument_group),
});
const taggedKey = (value) => {
  const key = String(value || '').trim();
  return key && key !== '-' ? key : null;
};

export function hasPositiveBars(value) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

export function formatLoopBars(value) {
  const bars = Number(value);
  return Number.isFinite(bars) && bars > 0 ? `${bars} bars` : '? bars';
}

export function chooseFillTargetBpm(packBpm, melodyCandidates = []) {
  if (isValidBpm(packBpm)) return Number(packBpm);
  const counts = new Map();
  melodyCandidates.forEach((loop) => {
    const loopBpm = Number(loop.bpm);
    if (!hasPositiveBars(loop.bars) || loop.bars !== 8 || !isValidBpm(loopBpm)) return;
    counts.set(loopBpm, (counts.get(loopBpm) || 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1]
    || Math.abs(a[0] - 90) - Math.abs(b[0] - 90)
    || a[0] - b[0])[0]?.[0] ?? null;
}

export function fisherYates(items, random = Math.random) {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.min(index, Math.floor(random() * (index + 1)));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled;
}

export function selectFillLoops({ group, candidates = [], existing = [], usedIds = new Set(), limit = LIMITS[group], targetBpm, targetKey, random = Math.random }) {
  const remaining = Math.max(0, limit - existing.length);
  if (!remaining) return [];
  const seen = new Set();
  const available = candidates.filter((loop) => {
    if (!hasPositiveBars(loop.bars) || usedIds.has(loop.id) || seen.has(loop.id)) return false;
    seen.add(loop.id);
    return true;
  });
  let preferredKey = group === 'melody'
    ? taggedKey(targetKey) || existing.map((loop) => taggedKey(loop.key)).find(Boolean)
    : null;
  if (group === 'melody' && !preferredKey) {
    const baseTiers = [
      available.filter((loop) => bpmsCompatible(loop.bpm, targetBpm) && Number(loop.bars) === 8),
      available.filter((loop) => bpmsCompatible(loop.bpm, targetBpm) && Number(loop.bars) !== 8),
      available.filter((loop) => !bpmsCompatible(loop.bpm, targetBpm) && Number(loop.bars) === 8),
      available.filter((loop) => !bpmsCompatible(loop.bpm, targetBpm) && Number(loop.bars) !== 8),
    ];
    preferredKey = baseTiers.map((tier) => fisherYates(tier, random).map((loop) => taggedKey(loop.key)).find(Boolean)).find(Boolean) || null;
  }
  const ordered = [];
  [true, false].forEach((compatible) => {
    [true, false].forEach((eightBars) => {
      const keyTiers = group === 'melody' && preferredKey ? [true, false] : [null];
      keyTiers.forEach((keyMatches) => {
        const tier = available.filter((loop) => (
          bpmsCompatible(loop.bpm, targetBpm) === compatible
          && (Number(loop.bars) === 8) === eightBars
          && (keyMatches == null || (taggedKey(loop.key) === preferredKey) === keyMatches)
        ));
        ordered.push(...fisherYates(tier, random));
      });
    });
  });
  return ordered.slice(0, remaining);
}

export async function fetchPagedClassLoops(group, { bpm, pageSize = FILL_PAGE_SIZE, cap = FILL_CATALOG_CAP, fetchPage = searchClassLoops } = {}) {
  const loops = [];
  let offset = 0;
  while (loops.length < cap) {
    const limit = Math.min(pageSize, cap - loops.length);
    const page = await fetchPage(group, { bpm, limit, offset });
    if (!Array.isArray(page)) throw new Error('Loop catalog returned an invalid page');
    loops.push(...page.slice(0, limit));
    if (page.length < limit) break;
    offset += limit;
  }
  return loops;
}

const compatibleTempoFilters = (targetBpm) => [...new Set([Number(targetBpm), Number(targetBpm) / 2, Number(targetBpm) * 2])]
  .filter((value) => Number.isInteger(value) && value > 0 && value <= 400);

async function fetchFillCandidates(group, targetBpm, needed) {
  let budget = FILL_CATALOG_CAP;
  const candidates = [];
  const ids = new Set();
  const append = (loops) => loops.forEach((loop) => {
    if (!ids.has(loop.id)) { ids.add(loop.id); candidates.push(loop); }
  });
  for (const compatibleBpm of compatibleTempoFilters(targetBpm)) {
    if (!budget) break;
    const page = await fetchPagedClassLoops(group, { bpm: compatibleBpm, cap: budget });
    budget -= page.length;
    append(page);
  }
  const usable = () => candidates.filter((loop) => hasPositiveBars(loop.bars)).length;
  if (usable() < needed && budget > 0) {
    const fallback = await fetchPagedClassLoops(group, { cap: budget });
    append(fallback);
  }
  return candidates;
}

function warningsFor(loop, bpm, melodyKeys) {
  const warnings = [];
  if (Number(loop.bars) !== 8) warnings.push({ label: 'not 8 bars', help: `This loop is ${formatLoopBars(loop.bars)}. It may end before or after the other loops.` });
  if (Number(loop.bpm) !== Number(bpm)) warnings.push({ label: 'BPM differs', help: `This loop is ${loop.bpm} BPM. It will be stretched to match the pack tempo.` });
  if (loop.instrument_group === 'melody' && loop.key && loop.key !== '-' && melodyKeys.size > 1) warnings.push({ label: 'key differs', help: 'This melody uses a different key, so some notes may clash.' });
  return warnings;
}

export function ClassPackBuilder() {
  const [packs, setPacks] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  // Blank means "Auto": the random fill picks the tempo with the most tagged 8-bar melodies.
  const [bpm, setBpm] = useState('');
  const [key, setKey] = useState('');
  const [slots, setSlots] = useState(emptySlots);
  const [catalog, setCatalog] = useState(emptySlots);
  const [search, setSearch] = useState({ melody: '', drums: '', texture: '', fx: '' });
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [filling, setFilling] = useState(null);
  const [fillError, setFillError] = useState('');
  const fillingRef = useRef(false);

  const refresh = useCallback(async () => {
    try { setPacks(await listClassPacks()); } catch (error) { setMessage(error.message); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    let active = true;
    Promise.all(CLASS_GROUPS.map(async (group) => [group, await searchClassLoops(group, { q: search[group], limit: 100, offset: 0 })]))
      .then((groups) => { if (active) setCatalog(Object.fromEntries(groups)); })
      .catch((error) => { if (active) setMessage(error.message); });
    return () => { active = false; };
  }, [search]);

  const melodyKeys = useMemo(() => new Set(slots.melody.map((loop) => loop.key).filter((value) => value && value !== '-')), [slots.melody]);
  const usedIds = useMemo(() => new Set(CLASS_GROUPS.flatMap((group) => slots[group].map((loop) => loop.id))), [slots]);

  const startNew = () => { setEditingId(null); setName(''); setBpm(''); setKey(''); setSlots(emptySlots()); setMessage(''); };
  const edit = (pack) => { setEditingId(pack.id); setName(pack.name); setBpm(pack.bpm); setKey(pack.key || ''); setSlots(pack.slots); setMessage(''); };
  const add = (group, loop) => {
    if (!hasPositiveBars(loop.bars) || slots[group].length >= LIMITS[group] || usedIds.has(loop.id)) return;
    const next = snapshotLoop(loop);
    setSlots((current) => ({ ...current, [group]: [...current[group], next] }));
    const firstLoop = CLASS_GROUPS.every((candidate) => slots[candidate].length === 0);
    if (((group === 'melody' && slots.melody.length === 0) || firstLoop) && Number.isFinite(Number(loop.bpm))) setBpm(Number(loop.bpm));
  };
  const remove = (group, id) => setSlots((current) => ({ ...current, [group]: current[group].filter((loop) => loop.id !== id) }));
  const move = (group, index, direction) => setSlots((current) => {
    const next = [...current[group]]; const target = index + direction;
    if (target < 0 || target >= next.length) return current;
    [next[index], next[target]] = [next[target], next[index]];
    return { ...current, [group]: next };
  });
  const fill = async (groups, fillId) => {
    if (fillingRef.current) return;
    fillingRef.current = true; setFilling(fillId); setFillError('');
    try {
      let targetBpm = isValidBpm(bpm) ? Number(bpm) : null;
      let pagedMelodies = null;
      if (targetBpm == null) {
        pagedMelodies = await fetchPagedClassLoops('melody');
        targetBpm = chooseFillTargetBpm('', pagedMelodies);
        if (targetBpm == null) throw new Error('No tagged 8-bar melodies were found.');
      }
      const candidatesByGroup = {};
      for (const group of groups) {
        const needed = Math.max(0, LIMITS[group] - slots[group].length);
        candidatesByGroup[group] = needed === 0
          ? []
          : group === 'melody' && pagedMelodies
          ? pagedMelodies
          : await fetchFillCandidates(group, targetBpm, needed);
      }
      const nextSlots = Object.fromEntries(CLASS_GROUPS.map((group) => [group, [...slots[group]]]));
      const nextUsedIds = new Set(usedIds);
      groups.forEach((group) => {
        const additions = selectFillLoops({ group, candidates: candidatesByGroup[group], existing: nextSlots[group], usedIds: nextUsedIds, targetBpm, targetKey: key }).map(snapshotLoop);
        nextSlots[group].push(...additions);
        additions.forEach((loop) => nextUsedIds.add(loop.id));
      });
      setSlots(nextSlots);
      if (!isValidBpm(bpm)) setBpm(targetBpm);
    } catch (error) {
      setFillError(error.message || "Couldn't load sounds for this fill.");
    } finally {
      fillingRef.current = false; setFilling(null);
    }
  };
  const fillGroup = (group) => fill([group], group);
  const fillAll = () => fill(CLASS_GROUPS, 'all');
  const save = async () => {
    if (!isValidBpm(Number(bpm))) { setMessage('Set a pack BPM, or use Fill the rest randomly to pick one.'); return; }
    setBusy(true); setMessage('');
    try {
      const body = { name, bpm: Number(bpm), key: key || null, slots };
      const saved = editingId ? await updateClassPack(editingId, body) : await createClassPack(body);
      setEditingId(saved.id); setMessage('Pack saved'); await refresh();
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };

  return <section className="class-pack-builder" data-testid="class-pack-builder">
    <div className="class-pack-heading"><div><h2>Class Packs</h2><p>Build the 50-loop palette for a class preview.</p></div><button onClick={startNew}>New pack</button></div>
    <div className="class-pack-intro"><img src="/pizzul/thinking.png" alt="Pizzul" /><p>A Class Pack is the 50 sounds a kid starts with: 20 melodies, 10 drums, 10 textures, 10 sound effects. Every loop is 8 bars so they line up. Keep one tempo and one key so they sound good together. In a hurry? Fill the rest randomly.</p></div>
    <div className="class-pack-list">
      {packs.map((pack) => <button key={pack.id} onClick={() => edit(pack)} className={editingId === pack.id ? 'active' : ''}>
        <strong>{pack.name}</strong><span>{pack.counts.melody}/20 · {pack.counts.drums}/10 · {pack.counts.texture}/10 · {pack.counts.fx}/10</span>
      </button>)}
      {!packs.length && <p>No class packs yet.</p>}
    </div>
    <div className="class-pack-form">
      <label>Pack name<input value={name} maxLength={80} onChange={(event) => setName(event.target.value)} /></label>
      <label>Pack BPM<input type="number" min="40" max="220" placeholder="Auto" value={bpm} onChange={(event) => setBpm(event.target.value)} /></label>
      {!isValidBpm(Number(bpm)) && <small className="class-pack-hint" data-testid="class-pack-bpm-hint">Auto: Fill the rest randomly picks the tempo with the most 8-bar melodies.</small>}
      <label>Key<input value={key} maxLength={32} onChange={(event) => setKey(event.target.value)} placeholder="Optional" /></label>
      <button onClick={fillAll} disabled={Boolean(filling)}>{filling === 'all' ? 'Loading sounds...' : 'Fill the rest randomly'}</button>
      {fillError && <span className="class-pack-fill-error" role="alert">{fillError}</span>}
    </div>
    <div className="class-pack-columns">
      {CLASS_GROUPS.map((group) => <div key={group} className="class-pack-column" style={{ '--class-group-color': CLASS_GROUP_META[group].color }}>
        <div className="class-pack-column-title"><h3>{CLASS_GROUP_META[group].label}</h3><span>{slots[group].length} / {LIMITS[group]}</span></div>
        <button onClick={() => fillGroup(group)} disabled={Boolean(filling)}>{filling === group ? 'Loading sounds...' : 'Fill the rest randomly'}</button>
        <input aria-label={`Search ${CLASS_GROUP_META[group].label}`} placeholder="Search loops" value={search[group]} onChange={(event) => setSearch((current) => ({ ...current, [group]: event.target.value }))} />
        {!slots[group].length && <p className="class-pack-empty">Nothing here yet. Tap Fill the rest randomly to get started.</p>}
        <div className="class-pack-selected">{slots[group].map((loop, index) => {
          const warnings = warningsFor(loop, bpm, melodyKeys);
          return <div key={loop.id}><span><strong>{loop.name}</strong>{warnings.length ? <small>{warnings.map((warning) => <span className="class-pack-warning" key={warning.label}>{warning.label}<button type="button" title={warning.help} aria-label={`Why ${warning.label}?`}>?</button></span>)}</small> : null}</span>
            <button aria-label={`Move ${loop.name} up`} onClick={() => move(group, index, -1)}>↑</button><button aria-label={`Move ${loop.name} down`} onClick={() => move(group, index, 1)}>↓</button><button aria-label={`Remove ${loop.name}`} onClick={() => remove(group, loop.id)}>×</button></div>;
        })}</div>
        <div className="class-pack-results">{(catalog[group] || []).filter((loop) => !usedIds.has(loop.id) && String(loop.name).toLowerCase().includes(search[group].trim().toLowerCase())).slice(0, 12).map((loop) => {
          const lengthTagged = hasPositiveBars(loop.bars);
          return <button key={loop.id} onClick={() => add(group, loop)} disabled={!lengthTagged || slots[group].length >= LIMITS[group]}><span>{loop.name}</span><small>{lengthTagged ? `${formatLoopBars(loop.bars)} · ${loop.bpm} BPM` : 'Length not tagged yet'}</small></button>;
        })}</div>
      </div>)}
    </div>
    <div className="class-pack-actions">
      {message && <span role="status">{message}</span>}
      {editingId && <button onClick={() => window.open(`/class?pack=${encodeURIComponent(editingId)}`, '_blank', 'noopener')}>Open class preview</button>}
      {editingId && <button onClick={async () => { await archiveClassPack(editingId); startNew(); await refresh(); }}>Archive</button>}
      <button className="primary" disabled={busy || !name.trim() || Number(bpm) < 40 || Number(bpm) > 220} onClick={save}>{busy ? 'Saving...' : 'Save pack'}</button>
    </div>
  </section>;
}

export default ClassPackBuilder;
